import { alpha, Box, Grid, Stack, Typography, useTheme } from "@mui/material";
import { RiskFactorsChip } from "~/components/RiskFactorsChip/RiskFactorsChip";
import { useFindingOnAsset } from "../../hooks";
import { getColor } from "~/lib/colors";
import { ImpactUpdated } from "~/components/impact/Updated/impact-updated";
import { ShowMoreSummary } from "~/components/ShowMore/ShowMoreSummary";
import { TagButtonRow } from "../Tags/TagButtonRow";
import { RiskFactorStats, ScoreRating } from "~/operations";
import { ScopeType } from "~/hooks/useScope";
import { TicketsSummary } from "~/pages/tickets/components/TicketsSummary/TicketsSummary";
import InfoTooltipButton from "~/components/InfoTooltipButton/InfoTooltipButton";
import RemediationJumpToButton from "~/components/Remediation/RemediationJumToButton/RemediationJumpToButton";
import { getSummaryText } from "./utils/getSummaryText";

const TOOLTIP_MAP: Record<ScoreRating, string> = {
  CRITICAL:
    "Presents extreme risk to your organization (risk score between 90 and 100)",
  ERROR: "A scan error occurred while evaluating this finding",
  HIGH: "Presents significant risk to your organization (risk score between 70 and 89)",
  MEDIUM:
    "Presents moderate risk to your organization (risk score between 40 and 69)",
  LOW: "Presents some risk to your organization (risk score between 1 and 39)",
  NONE: "Presents no risk to your organization (risk score of 0)",
  UNSCORED: "Presents an unknown risk to your organization until scored",
};

/**
 * Safely extracts risk factors from a finding
 * @param riskFactorsData - The potentially nested risk factors array from the finding
 * @returns A flattened array of risk factors with null/undefined values removed
 */
const extractRiskFactors = (
  riskFactorsData?: Array<RiskFactorStats | null> | null,
): RiskFactorStats[] => {
  if (!riskFactorsData) {
    return [];
  }

  // Remove null/undefined items and return clean array
  return riskFactorsData.filter(Boolean) as RiskFactorStats[];
};

export function SummaryData() {
  const theme = useTheme();
  const {
    finding,
    labelTags,
    ticketActions,
    ticketsCount,
    activeScope,
    advisoryData,
  } = useFindingOnAsset();
  const { asset } = finding || {};

  const { checkRemediation } = useFindingOnAsset();
  const hasRemediations = checkRemediation.entries.length > 0;

  const riskFactors = extractRiskFactors(finding?.riskFactors);

  if (
    activeScope?.type !== ScopeType.Workspace &&
    activeScope?.type !== ScopeType.Space
  ) {
    // We only want to render this component for workspace or space scopes.
    return null;
  }

  const primaryScoreColor = getColor(theme, String(finding?.rating));
  const ratingTooltip = finding?.rating ? TOOLTIP_MAP[finding.rating] : "";

  const hasRiskFactors = !!riskFactors.length;
  const summaryText = getSummaryText(finding, {
    advisoryDescription: advisoryData?.description,
  });

  return (
    <Grid container columnSpacing={4} rowSpacing={2} mt={1}>
      {/*  TICKET ROW  */}
      {ticketActions.hasListTicketsPermissions && ticketsCount > 0 && (
        <Grid size={12} mt={-1}>
          <TicketsSummary
            count={ticketsCount}
            scope={activeScope}
            assetId={asset?.id}
          />
        </Grid>
      )}

      {/*  LEFT SIDE DESCRIPTION  */}
      <Grid
        container
        spacing={1}
        size={{ xs: 12, md: "grow" }}
        order={{ xs: 2, md: 1 }}
      >
        {/* Risk Factors Chips */}
        {hasRiskFactors && (
          <Grid size={12}>
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {riskFactors.map((riskFactor) => (
                <RiskFactorsChip riskFactor={riskFactor} key={riskFactor.mrn} />
              ))}
            </Stack>
          </Grid>
        )}
        {/* Summary */}
        <Grid size={12}>
          <TagButtonRow items={labelTags} />
          {finding?.title && (
            <Grid size={12}>
              <ShowMoreSummary
                maxHeight={270}
                text={summaryText}
                TextProps={{
                  fontWeight: 500,
                  overflow: "auto",
                  lineHeight: 1.6,
                }}
              />
            </Grid>
          )}
          {hasRemediations && <RemediationJumpToButton />}
        </Grid>
      </Grid>

      {/*  RIGHT SIDE - SCORES BOX */}
      <Grid size={{ xs: 12, md: "auto" }} order={{ xs: 1, md: 2 }}>
        <Box
          sx={{
            p: 3,
            width: { xs: "100%", md: 320 },
            maxWidth: { xs: "100%" },
            background: alpha(primaryScoreColor, 0.1),
            borderRadius: 1,
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Stack direction="column" alignItems={{ xs: "left" }} gap={2}>
              <Stack direction="row" alignItems="start" spacing={0.5}>
                <Typography
                  sx={{
                    fontSize: 24,
                    fontWeight: 700,
                    lineHeight: "24px",
                    letterSpacing: ".96px",
                  }}
                >
                  {finding?.rating}
                </Typography>
                <InfoTooltipButton title={ratingTooltip} />
              </Stack>
              <ImpactUpdated
                impact={{
                  rating: String(finding?.rating),
                  value: finding?.riskValue || 0,
                }}
                options={{
                  size: 16,
                  border: 2,
                }}
                showText={false}
                isRiskScore
                hideTooltip
              />
            </Stack>
            <Typography
              sx={{
                color: primaryScoreColor,
                fontSize: 48,
                fontWeight: 700,
                lineHeight: "48px",
              }}
            >
              {finding?.riskValue}
            </Typography>
          </Stack>
        </Box>
      </Grid>
    </Grid>
  );
}
